import React, { useEffect, useState } from 'react'
import { Row, Col, Card, Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import CategoryTree from '../components/CategoryTree'
import CategoryDetailsForm from '../components/CategoryDetailsForm'
import { fetchRootCategories, fetchChildren, createCategoryAdmin, updateCategoryAdmin, deleteCategoryAdmin } from '../slices/categorySlice'

export default function AdminCategoryManagerScreen() {
  const dispatch = useDispatch()
  const history = useHistory()
  const userInfo = useSelector((s) => s.user?.userInfo)
  const { byId, childrenByParent, roots, error } = useSelector((s) => s.categories || {})
  const [selectedId, setSelectedId] = useState(null)
  const [expanded, setExpanded] = useState(new Set())
  const [pending, setPending] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (!userInfo || !userInfo.isAdmin) {
      history.push('/login')
      return
    }
    if (!roots || roots.length === 0) dispatch(fetchRootCategories())
  }, [userInfo, history, dispatch, roots])

  const handleToggle = (id) => {
    const next = new Set(expanded)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setExpanded(next)
    dispatch(fetchChildren(id))
  }

  const handleAddSub = async (parentId, name) => {
    setPending(true); setErr(null)
    try {
      await dispatch(createCategoryAdmin({ name, parentId })).unwrap()
      // Refresh children of parent and roots if adding to root
      if (parentId) await dispatch(fetchChildren(parentId))
      else await dispatch(fetchRootCategories())
    } catch (e) { setErr(e?.message || String(e)) }
    finally { setPending(false) }
  }

  const handleRename = async (id, name) => {
    setPending(true); setErr(null)
    try {
      await dispatch(updateCategoryAdmin({ id, name })).unwrap()
    } catch (e) { setErr(e?.message || String(e)) }
    finally { setPending(false) }
  }

  const handleDelete = async (id) => {
    setPending(true); setErr(null)
    try {
      await dispatch(deleteCategoryAdmin({ id })).unwrap()
      // Clear selection if deleted
      if (selectedId === id) setSelectedId(null)
      // Refresh roots and possibly parent branch
      const parentId = byId[id]?.parent || null
      if (parentId) await dispatch(fetchChildren(parentId))
      else await dispatch(fetchRootCategories())
    } catch (e) { setErr(e?.message || String(e)) }
    finally { setPending(false) }
  }

  return (
    <div>
      <Row className='mb-3'>
        <Col><h1>Manage Categories</h1></Col>
        <Col className='text-end'>
          <Link to='/admin/productlist' className='btn btn-light my-1'>
            {' '}back to product list
          </Link>
        </Col>
      </Row>
      <Row>
        <Col md={5}>
          <Card>
            <Card.Header>Category Tree</Card.Header>
            <Card.Body>
              <CategoryTree
                byId={byId}
                childrenByParent={childrenByParent}
                rootIds={childrenByParent?.['null'] || roots || []}
                expandedIds={expanded}
                onToggleExpand={handleToggle}
                onSelectNode={setSelectedId}
                selectedId={selectedId}
              />
              <div className='mt-3'>
                <Button variant='secondary' size='sm' onClick={() => handleAddSub(null, 'New Category')} disabled={pending}>+ Add Root Category</Button>
              </div>
              {error && <div className='text-danger small mt-2'>{error}</div>}
              {err && <div className='text-danger small mt-2'>{err}</div>}
            </Card.Body>
          </Card>
        </Col>
        <Col md={7}>
          <Card>
            <Card.Header>Details</Card.Header>
            <Card.Body>
              <CategoryDetailsForm
                node={selectedId ? byId[selectedId] : null}
                onAddSub={(name) => selectedId ? handleAddSub(selectedId, name) : handleAddSub(null, name)}
                onRename={(name) => selectedId && handleRename(selectedId, name)}
                onDelete={() => selectedId && handleDelete(selectedId)}
                disableSlugEdit
                pending={pending}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
